import {LayoutProps} from "components/layout";
import {DrupalNode, DrupalTaxonomyTerm} from "next-drupal";
import {useEffect, useState, Fragment, useContext} from "react";
import classNames from "classnames";
import {Dictionary, DrupalWebform, SupportTerm} from "types/hygiena-types";
import {Link} from "@/components/atoms/link";
import {getKvpPair} from "@/lib/get-kvp-pair";
import {FormattedText} from "@/components/formatted-text";

import dynamic from "next/dynamic";
import {DictionaryContext} from "@/context/dictionary-context";
const WebformTechnicalSupportRequest = dynamic(() => import("components/templates/webform/webform--technical-support-request").then((mod) => mod.WebformTechnicalSupportRequest), {ssr: false});
const ImageIcon = dynamic(() => import("components/atoms/icon").then((mod) => mod.ImageIcon));
const SectionHeading = dynamic(() => import("../../../organisms/section-heading"));
const ScrollLink = dynamic(() => import("../../../atoms/scroll-link").then((mod) => mod.ScrollLink));
const MediaImage = dynamic(() => import("components/templates/media/media--image").then((mod) => mod.MediaImage));

interface PageProps extends LayoutProps {
  node: DrupalNode,
  faqs: DrupalNode[],
  supportTerms: SupportTerm[],
  faqCategoryTerms: DrupalTaxonomyTerm[],
  technicalSupportWebform?: DrupalWebform,
}

function FAQItem({node, activeParent}: {node: DrupalNode, activeParent: boolean}) {
  const [active, setActive] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Debounce an additional expanded state; so we don't collapse the
    // accordion content too soon, and un-rerender it after it has closed.
    const update = setTimeout(() => {
      setExpanded(active);
    }, active ? 0 : 200);

    return () => {
      clearTimeout(update);
    }
  }, [active]);

  /**
   * Create a simple triangle.
   *
   * @constructor
   */
  function Expander(): JSX.Element {
    return <span className={classNames(
      "inline-block pr-4 transition-all duration-500 ease-in-out text-2xl text-primary font-bold",
    )}>{active ? "-" : "+"}</span>
  }

  return (
    <button className={classNames(
      "faq-topic-link bg-white px-2 lg:px-12 py-2 text-lg font-normal cursor-pointer border-l-4 transition-all w-full text-left",
      {"border-white": !activeParent},
      {"border-primary": activeParent},
      {"font-bold text-primary": active})
    } key={node.id} onClick={() => setActive(!active)} role="button">
      <div className="flex items-baseline">
        <Expander/>
        <div className="border-b border-hygienaDarkGray w-full pb-4">
          <div className="faq-topic-title">{node.title}</div>
          <div className={classNames(
            "overflow-hidden transition-all duration-200 px-0 lg:px-8",
            {"max-h-0": !active},
            {"max-h-screen": active}
          )}>
            <div className="pt-4 font-normal text-black">
              {expanded && node?.body?.processed && (
                <FormattedText processed={node.body.processed}/>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function FAQContainer({term, items, childItems}: {term: DrupalTaxonomyTerm, items: DrupalNode[], childItems: any}): JSX.Element {
  const [active, setActive] = useState(false);

  /**
   * Create a simple triangle.
   *
   * @constructor
   */
  function Triangle({active}: {active: boolean}): JSX.Element {
    return <span className={classNames(
      "flex pr-4 transition-all duration-500 ease-in-out",
      {"transform rotate-90 -translate-x-5 translate-y-1.5": active},
    )}>
      <ImageIcon type={"chevron-right"} color={"black"} height={25} width={25} alt="Expand or Collapse"/>
    </span>
  }

  return (
    <div id="frequently-asked-questions" className={classNames("cursor-pointer")}>
      <button className="flex w-full h-full py-5" onClick={() => setActive(!active)} role="button">
        <h3 className="text-black px-8 text-left font-light flex-grow">
          {term.name}
        </h3>
        <Triangle active={active}/>
      </button>
      <div className={classNames(
        "overflow-hidden transition-all delay-200 ease-linear origin-top py-4",
        {"hidden": !active},
      )}>
        {items.map(item => (
          <FAQItem key={item.id} node={item} activeParent={active}/>
        ))}
        {Object.keys(childItems).map((name, idx) => {
          return (
            <Fragment key={`faq-${name}-${idx}`}>
              <div className={classNames(
                "bg-white px-7 py-2 text-primary font-bold text-lg cursor-pointer border-l-4 border-primary transition-all"
              )}>
                <div>{childItems[name].term.name}</div>
              </div>
              {childItems[name].items.map( child => (
                <FAQItem key={child.id} node={child} activeParent={active}/>
              ))}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export function SupportItemsSection({terms}: {terms: SupportTerm[]}): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {terms.map(term => (
        <Link key={term.id} className="p-8 drop-shadow-sm hover:drop-shadow-md rounded-md bg-hygienaLight" href={term?.field_support_link?.uri?.replace(/^internal:/, "") ?? "/"}>
          <div className="flex items-center gap-2">
            <div>
              {term?.field_support_icon && (
                <MediaImage
                  height={100}
                  width={100}
                  media={term.field_support_icon}
                  sizes="(min-width: 968px) 50px, (min-width: 768px) 50px, 50px"
                />
              )}
            </div>
            <div>
              <h4 className="text-primary font-semibold md:!text-2xl pb">{term.name}</h4>
              {term?.description?.processed && <FormattedText processed={term.description.processed}/>}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

/**
 * A support page region card.
 *
 * @param title
 *   The region name.
 * @param icon
 *   The icon type.
 * @param phone
 *   An optional phone number.
 * @param select
 *   An option to select when scrolling to the tech support form.
 * @param calendarLink
 *   An optional calendar link, must be paired with calendarText.
 * @param calendarText
 *   Optional calendar text, must be paired with calendarLink.
 * @constructor
 */
export function SupportRegionCard({title, icon, phone = "", select = "", calendarLink = "", calendarText = ""}) {
  const t = useContext<Dictionary>(DictionaryContext);

  /**
   * A callback to pre-select the region.
   */
  const selectRegion = () => {
    const element = document.getElementById('form--technical-support-request--region') as HTMLSelectElement;
    if (element && select?.length) {
      element.value = select;
    }
  }

  let iconName: string = '';
  switch (icon) {
    case 'asia_pacific':
    case 'globe-pacific':
      iconName = 'globe-pacific';
      break;
    case 'emea':
    case 'globe-emea':
      iconName = 'globe-emea';
      break;
    case 'americas':
    case 'globe-americas':
    default:
      iconName = 'globe-americas';
      break;
  }

  return (
    <div className="p-8 rounded-md drop-shadow-sm bg-white flex-1 flex flex-col">
      <div className="text-center mb-4">
        <ImageIcon type={iconName} color={"black"} height={50} width={50} alt={icon} className="mx-auto mb-3"/>
        <h3 className="pb-4">{title}</h3>
      </div>
      <div className="flex justify-between flex-col flex-1">
        <div className="flex gap-2 mb-3 items-center font-light">
          {calendarLink?.length && calendarText?.length ? (
            <Link href={calendarLink ?? "/"} className="flex items-center gap-[7px] font-bold text-primary" target="_blank">
              <ImageIcon type={"calendar"} color={"black"} height={25} width={25}  alt={calendarText}/>
              <span className="text-sm md:text-base">{calendarText}</span>
            </Link>
          ): <></>}
        </div>
        <div className="flex gap-2 mb-3 items-center font-light">
          {phone?.length ? (
            <>
              <ImageIcon type={"phone"} color={"black"} height={25} width={25}  alt={phone}/>
              <span className="text-sm md:text-base">{phone}</span>
            </>
          ): <></>}
        </div>
        <div className="flex gap-2 items-center font-bold text-primary">
          <ImageIcon type={"envelope"} color={"black"} height={25} width={25}  alt={`${t?.all?.contact_tech_support}`}/>
          <ScrollLink id={"tech-support"} className={"flex gap-1"} callback={selectRegion}>
            <span className="text-sm md:text-base">{`${t?.all?.contact_tech_support}`}</span>
            <ImageIcon type="arrow-down" color="primary" alt="down" height={10} width={20} style={{height: "25px", width: "25px"}}/>
          </ScrollLink>
        </div>
      </div>
    </div>
  )
}

export function ContentSupportPage({node, faqs, faqCategoryTerms, supportTerms, technicalSupportWebform}: PageProps): JSX.Element {
  const categories = {};
  const faqHeaderText = getKvpPair(node, "faq_header")?.value;
  const t = useContext<Dictionary>(DictionaryContext);

  // Build out a 2 layer level of FAQ categories.
  // @todo there is a better way to structure this.
  for (const term of faqCategoryTerms) {
    const parent = term?.parent?.[0];
    if (parent?.id !== "virtual") {
      if (!categories[parent.name] || Array.isArray(categories[parent.name])) {
        categories[parent.name] = {term: parent, children: {}, items: []};
      }
      categories[parent.name].children[term.name] = {term, items: []};
    }
    else {
      if (!categories[term.name]) {
        categories[term.name] = {term: term, children: {}, items: []};
      }
    }
  }

  // Build out a category map of FAQs.
  for (const item of faqs) {
    if (item?.field_faq_category) {
      for (const category of item.field_faq_category) {
        const parent = category?.parent?.[0];
        if (parent?.id !== "virtual") {
          categories[parent.name].children[category.name].items.push(item);
        }
        else {
          categories[category.name].items.push(item);
        }
      }
    }
  }

  return (
    <>
      <div className="container px-6 lg:px-0 mx-auto text-black pb-12">
        <div className="pb-8 mt-12">
          <SectionHeading title={faqHeaderText}/>
        </div>
        <div className="container mx-auto drop-shadow-sm bg-white rounded-md">
          {Object.keys(categories).sort().map((key, index) => {
            if (!categories[key].items.length && !Object.keys(categories[key].children).length) {
              return;
            }
            return <FAQContainer key={key} term={categories[key].term} items={categories[key].items} childItems={categories[key].children}/>
          })}
        </div>
      </div>
      <div className="container px-6 lg:px-0 mx-auto text-black">
        <div className="pb-8 mt-12 scroll-m-28" id="tech-support">
          <SectionHeading title={t?.all?.technical_support_request ?? "Technical Support Request"}/>
          {technicalSupportWebform && <WebformTechnicalSupportRequest webform={technicalSupportWebform}/>}
        </div>
      </div>
    </>
  );
}
